import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Dialog } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Field } from '@/components/ui/field';
import { LoadingState, ErrorState } from '@/components/ui/states';
import { useAuth } from '@/Hooks/useAuth';
import { apiMessage } from '../helpers';
import type { StaffDetail } from '../types';

interface Props {
  open: boolean;
  staffId: string | null;
  staffName: string;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * PIN and password administration (§13). Nothing here ever renders a secret:
 * `GET /users/:id` answers `hasPassword` / `hasPin` booleans and the hashes
 * stay behind `USER_SELECT`, so the dialog can only say whether a credential
 * exists — never what it is.
 */
export default function StaffCredentialsDialog({
  open,
  staffId,
  staffName,
  onClose,
  onSaved,
}: Props) {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [detail, setDetail] = useState<StaffDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [pin, setPin] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  const isSelf = Boolean(staffId && user?.id === staffId);

  const load = () => {
    if (!staffId) return;
    setLoading(true);
    setLoadError(null);
    api
      .get<StaffDetail>(`/users/${staffId}`)
      .then(({ data }) => setDetail(data))
      .catch((error) => setLoadError(apiMessage(error, t('settings.errors.loadFailed'))))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open || !staffId) return;
    setPin('');
    setCurrentPassword('');
    setNewPassword('');
    setDetail(null);
    load();
    // `load` is recreated every render; the identity that matters is the id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, staffId]);

  const run = async (label: string, action: () => Promise<unknown>, success: string) => {
    setBusy(label);
    try {
      await action();
      toast.success(success);
      load();
      onSaved();
    } catch (error) {
      toast.error(apiMessage(error, t('settings.errors.saveFailed')));
    } finally {
      setBusy(null);
    }
  };

  /**
   * `SetPinDto` matches 4–6 digits and `ChangePasswordDto` demands 8 characters,
   * and a password you are changing for YOURSELF has to prove the current one
   * (`UsersService.changePassword`). Each is stated on the control rather than
   * discovered by pressing it.
   */
  const pinBlock = /^\d{4,6}$/.test(pin) ? null : t('settings.credentials.pinRuleReason');
  const passwordBlock =
    newPassword.length < 8
      ? t('settings.credentials.passwordRuleReason')
      : isSelf && !currentPassword
        ? t('settings.credentials.currentPasswordRequired')
        : null;

  const savePin = () => {
    if (pinBlock) {
      toast.error(t('settings.validation.pinFormat'));
      return;
    }
    void run(
      'pin',
      () => api.put(`/users/${staffId}/pin`, { pin }).then(() => setPin('')),
      t('settings.credentials.pinSaved'),
    );
  };

  const clearPin = () =>
    void run('clearPin', () => api.delete(`/users/${staffId}/pin`), t('settings.credentials.pinCleared'));

  const savePassword = () => {
    if (passwordBlock) {
      toast.error(passwordBlock);
      return;
    }
    void run(
      'password',
      () =>
        api
          .put(`/users/${staffId}/password`, {
            ...(isSelf ? { currentPassword } : {}),
            newPassword,
          })
          .then(() => {
            setCurrentPassword('');
            setNewPassword('');
          }),
      t('settings.credentials.passwordSaved'),
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('settings.credentials.title')}
      description={staffName}
      footer={
        <Button variant="outline" onClick={onClose}>
          {t('common.close')}
        </Button>
      }
    >
      {loading ? (
        <LoadingState label={t('common.loading')} />
      ) : loadError ? (
        <ErrorState message={loadError} onRetry={load} />
      ) : (
        <div className="space-y-6">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Badge tone={detail?.hasPassword ? 'success' : 'neutral'}>
              {detail?.hasPassword
                ? t('settings.credentials.passwordSet')
                : t('settings.credentials.noPassword')}
            </Badge>
            <Badge tone={detail?.hasPin ? 'success' : 'neutral'}>
              {detail?.hasPin ? t('settings.credentials.pinSet') : t('settings.credentials.noPin')}
            </Badge>
          </div>

          <section className="space-y-3 rounded-lg border p-4">
            <h3 className="text-sm font-semibold">{t('settings.credentials.pinSection')}</h3>
            <Field label={t('settings.staff.pin')} hint={t('settings.staff.pinHint')}>
              <Input
                inputMode="numeric"
                autoComplete="off"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                dir="ltr"
              />
            </Field>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                onClick={savePin}
                disabled={busy !== null || pinBlock !== null}
                title={pinBlock ?? undefined}
              >
                {detail?.hasPin ? t('settings.credentials.resetPin') : t('settings.credentials.setPin')}
              </Button>
              {detail?.hasPin && (
                <Button size="sm" variant="outline" onClick={clearPin} disabled={busy !== null}>
                  {t('settings.credentials.clearPin')}
                </Button>
              )}
              {pinBlock && (
                <span className="self-center text-xs text-ink-muted">
                  {pinBlock}
                </span>
              )}
            </div>
          </section>

          <section className="space-y-3 rounded-lg border p-4">
            <h3 className="text-sm font-semibold">{t('settings.credentials.passwordSection')}</h3>
            {isSelf && (
              <Field label={t('settings.credentials.currentPassword')} required>
                <Input
                  type="password"
                  autoComplete="current-password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                />
              </Field>
            )}
            <Field
              label={t('settings.credentials.newPassword')}
              hint={t('settings.staff.passwordHint')}
              required
            >
              <Input
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </Field>
            <p className="text-xs text-ink-muted">
              {t('settings.credentials.passwordWarning')}
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Button
                size="sm"
                onClick={savePassword}
                disabled={busy !== null || passwordBlock !== null}
                title={passwordBlock ?? undefined}
              >
                {t('settings.credentials.changePassword')}
              </Button>
              {passwordBlock && (
                <span className="text-xs text-ink-muted">{passwordBlock}</span>
              )}
            </div>
          </section>
        </div>
      )}
    </Dialog>
  );
}
